import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import type { NewBillToForm } from '../fixtures';
import Card from './Card';
import Row from '../../pro/components/orderform/Row';
import { Label } from '../../pro/components/orderform/Label';
import TextInput from '../../pro/components/orderform/TextInput';

const LBL = 116;

interface Props {
  initialName: string;
  onClose: () => void;
  onCreated: (form: NewBillToForm) => void;
}

function initialForm(name: string): NewBillToForm {
  return { name, address1: '', city: '', state: '', zip: '', contact: '', phone: '', fax: '' };
}

// V1 inline_quote.pl upperit: name/address/city/state/zip/contact are upper-cased
// (phone/fax are not). Applied on save so the deferred bill-to's typed values
// match how a real billtos master would be stored.
const UPPER_FIELDS: ReadonlyArray<keyof NewBillToForm> = ['name', 'address1', 'city', 'state', 'zip', 'contact'];

/**
 * "Add As New Bill To" modal behind the create wizard's bill-to picker (V1
 * inline_quote.pl:356-405). V1 never inserts a cms.billtos row — it defers: the
 * quote carries billto=0 with the typed name/address. This modal makes NO API
 * call; it hands the typed form back via onCreated and the wizard sets billto=0.
 */
const NewBillToModal: React.FC<Props> = ({ initialName, onClose, onCreated }) => {
  const [form, setForm] = useState<NewBillToForm>(() => initialForm(initialName));
  const patch = (p: Partial<NewBillToForm>) => setForm((f) => ({ ...f, ...p }));

  // Esc closes this modal only (capture-phase so the wizard's own Esc handler
  // doesn't also fire its discard-draft confirm underneath).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valid = form.name.trim().length > 0;

  function handleSave() {
    if (!valid) return;
    const out: NewBillToForm = { ...form };
    for (const k of Object.keys(out) as Array<keyof NewBillToForm>) {
      const v = out[k].trim();
      out[k] = UPPER_FIELDS.includes(k) ? v.toUpperCase() : v;
    }
    onCreated(out);
  }

  // Rendered only while open. Alt+C closes; creating the bill-to goes through a
  // native form submit, so Enter already covers it.
  useAltHotkeys({ C: onClose }, undefined, { layer: true });

  // Rendered only while open (parent unmounts on close) — unmount resets the drag.
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4 overflow-y-auto"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="w-full max-w-[520px] max-h-[85vh] overflow-y-auto bg-white border border-slate-200 rounded-[10px] shadow-2xl dark:bg-slate-800 dark:border-slate-700"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="New Bill To"
      >
        <ModalHeader
          title={
            <span className="inline-flex items-center gap-2">
              <UserPlusIcon className="h-4 w-4 text-blue-300" /> New Bill To
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          className="sticky top-0 z-10 rounded-t-[10px]"
        />

        <div className="px-4 py-3 flex flex-col gap-3">
          <Card icon={<UserPlusIcon className="h-3.5 w-3.5" />} title="Bill To">
            <Row><Label width={LBL} highlight="red">Name</Label><TextInput value={form.name} onChange={(v) => patch({ name: v })} ariaLabel="Bill-To Name" /></Row>
            <Row><Label width={LBL}>Address</Label><TextInput value={form.address1} onChange={(v) => patch({ address1: v })} ariaLabel="Address" /></Row>
            <Row>
              <Label width={LBL}>City / St / Zip</Label>
              <TextInput value={form.city} onChange={(v) => patch({ city: v })} ariaLabel="City" />
              <TextInput value={form.state} onChange={(v) => patch({ state: v })} ariaLabel="State" width={48} />
              <TextInput value={form.zip} onChange={(v) => patch({ zip: v })} ariaLabel="Zip" width={80} />
            </Row>
            <Row><Label width={LBL}>Contact</Label><TextInput value={form.contact} onChange={(v) => patch({ contact: v })} ariaLabel="Contact" /></Row>
            <Row><Label width={LBL}>Phone</Label><TextInput value={form.phone} onChange={(v) => patch({ phone: v })} ariaLabel="Phone" /></Row>
            <Row><Label width={LBL}>Fax</Label><TextInput value={form.fax} onChange={(v) => patch({ fax: v })} ariaLabel="Fax" /></Row>
          </Card>
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 dark:border-slate-700 dark:bg-slate-800">
          <button type="button" onClick={onClose} className="px-3.5 py-1.5 text-[0.78125rem] font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600">Cancel</button>
          <button
            type="button" onClick={handleSave} disabled={!valid}
            className="px-4 py-1.5 text-[0.78125rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded-md disabled:opacity-40 hover:bg-blue-700"
          >Create Bill To</button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NewBillToModal;
